// PBR press logo marquee — keeps the scroll at a constant speed (data-speed,
// px/s) whatever the number or size of logos. The Liquid renders two
// identical halves; the track slides one half-width per loop. This element
// repeats the logo set ([data-set-item]) until each half is at least as wide
// as the viewport (so no blank run shows with only a logo or two), then sets
// the loop duration to half-width / speed. Re-measures on resize and as
// lazy logos load.

class PbrLogoMarquee extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('.pbr-press-logos__track');
    this.halves = Array.from(this.querySelectorAll('.pbr-press-logos__half'));
    this.setItems = Array.from(this.querySelectorAll('[data-set-item]'));
    if (!this.track || this.halves.length !== 2 || this.setItems.length === 0) return;

    this.speed = parseFloat(this.dataset.speed) || 50;
    this.reps = parseInt(this.dataset.reps, 10) || 1;

    this.observer = new ResizeObserver(() => this.#update());
    this.observer.observe(this);
    this.observer.observe(this.halves[0]);
    this.#update();
  }

  disconnectedCallback() {
    this.observer?.disconnect();
  }

  #update() {
    const half = this.halves[0];
    const halfWidth = half.getBoundingClientRect().width;
    if (!halfWidth) return;

    // Each rep contributes the same run of items + gaps, so one set's width is
    // an exact share of the half (the half's trailing padding equals a gap).
    const setWidth = halfWidth / this.reps;
    const needed = Math.max(1, Math.ceil(this.clientWidth / setWidth));

    if (needed !== this.reps) {
      this.#build(needed);
      return; // the half's resize fires the observer again with the new width
    }

    this.track.style.setProperty('--pbr-logos-duration', `${(halfWidth / this.speed).toFixed(2)}s`);
  }

  #build(reps) {
    this.reps = reps;
    this.halves.forEach((half, h) => {
      const nodes = [];
      for (let r = 0; r < reps; r++) {
        this.setItems.forEach((item) => {
          // Keep the originals (they carry the Customizer block attributes)
          // as the first run of the first half; everything else is a copy.
          if (h === 0 && r === 0) {
            nodes.push(item);
            return;
          }
          const copy = item.cloneNode(true);
          copy.removeAttribute('data-set-item');
          copy.removeAttribute('data-shopify-editor-block');
          nodes.push(copy);
        });
      }
      half.replaceChildren(...nodes);
    });
  }
}

if (!customElements.get('pbr-logo-marquee')) {
  customElements.define('pbr-logo-marquee', PbrLogoMarquee);
}
